import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import moment from "moment";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import { debounceTime, filter, map, switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { DataStorage } from "src/model/class/general classes/data-storage";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import {
  WeeklyPlanning,
  WeeklyPlanningCopyData,
} from "src/model/class/general classes/weekly-planning";
import { WeeklyPlanningAction } from "src/model/class/general classes/weekly-planning-action";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { iWeeklyPlanning } from "src/model/interface/iWeekly-planning";
import { iWeeklyPlanningAction } from "src/model/interface/iWeekly-planning-action";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})

/**
 *
 *  weeklyPlanning service manage data about weeklyPlanning
 *
 *
 */
export class WeeklyPlanningService
  extends DataStorage
  implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "weekly-planning.service";

  private _weeklyPlanningStore: GenericApiData = null;
  private _weeklyPlanningSubject: BehaviorSubject<Array<WeeklyPlanning>> =
    new BehaviorSubject<Array<WeeklyPlanning>>(null);

  private _isErrorInGetWeeklyPlanningListAPIRequest: boolean = false;

  get isErrorInGetWeeklyPlanningListAPIRequest(): boolean {
    return this._isErrorInGetWeeklyPlanningListAPIRequest;
  }

  /**
   * Currently selected building, kept in sync with the ngrx store. Its {@link Building.idAreaLocal}
   * is the area the weekly-planning list is scoped to and is sent as the `idArea` query parameter
   * on every list request.
   */
  private _currentBuilding: Building = null;
  private _buildingSubscription: Subscription = null;

  private _actionsKey: string[] = [];

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  /**
   * Map to locally track, for each existing "weekly-planning" object, the current form of the first "WeeklyPlanningActions" for each week-day.
   * The form of the map is the following:
   *
   * KEY: ID of a particular "weekly-planning" object
   *
   * VALUE: a matrix 7x4 with
   *  * 7 columns: One for each week day, form Monday to Sunday
   *  * 4 rows: One for each of the first four WeeklyPlanningActions of each week-day;
   *            these Actions correpsond to 15-minutes intervals starting at hour 00:00, 00:15. 00:30, 00:45
   *
   * This map is useful to allows for the correct creation/update of the final time-slice defined for the current week-day, therefore ending at hour 24:00.
   * It is updated each time actions are retrieved from server (for single days or goup of them).
   */
  private _weeklyPlanningsDaysFirstActionsMap: Map<
    number,
    Array<Array<WeeklyPlanningAction>>
  > = null;

  private _mutex = new Mutex();

  public needToInitialize: boolean = true;

  public /*readonly*/ weeklyPlanningList =
    this._weeklyPlanningSubject.asObservable();

  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _storageService: LocalStorageService,
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _mqttServiceNative: MqttService,
    private _store: Store
  ) {
    super();

    this.subscribeBuilding();

    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();
    this._weeklyPlanningsDaysFirstActionsMap = new Map<
      number,
      Array<Array<WeeklyPlanningAction>>
    >();

    /** Subscrive event from Event Service */
    //this.subscribeEvent();

    this.subscribeEvents();

    this.subscribeMQTTAddresses();

    this._eventService.OnComeBackOnline.subscribe(() => {
      this.initializeweeklyPlanningList();
    });
  }

  /** Destroy event service */
  ngOnDestroy() {
    this._weeklyPlanningSubject.next(null);
    this._weeklyPlanningSubject.complete();

    //this.eventService.OnMakeLogout.unsubscribe();
    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();

    if (this._buildingSubscription) {
      this._buildingSubscription.unsubscribe();
    }

    this._weeklyPlanningsDaysFirstActionsMap = null;
  }

  /**
   * Keep {@link _currentBuilding} in sync with the currently selected building, so the area the
   * list is scoped to is always available when a request is issued.
   */
  private subscribeBuilding(): void {
    this._buildingSubscription = this._store
      .select(selectedBuilding)
      .subscribe((currentBuilding: Building) => {
        this._currentBuilding = currentBuilding;
      });
  }

  /**
   * Id of the area the weekly-planning list is currently scoped to, or null if no building is
   * selected yet.
   */
  private getCurrentIdArea(): number {
    return this._currentBuilding ? this._currentBuilding.idAreaLocal : null;
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    /** Operation performed on logout */ //
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_WEEKLYPLANNING);

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "OnMakeLogout",
          "subscribeEvents",
          " IN OnMakeLogout"
        );

        await this.unsubscribeToMQTTAddresses();

        //Emptying the local map
        for (const entry of Array.from(
          this._weeklyPlanningsDaysFirstActionsMap.entries()
        )) {
          this._weeklyPlanningsDaysFirstActionsMap.delete(entry[0]);
        }

        for (let i = 0; i < this._actionsKey.length; i++) {
          this._storageService.remove(this._actionsKey[i]);
        }

        this._weeklyPlanningStore = null;
        this._weeklyPlanningSubject.next(null);
        this.needToInitialize = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        if (this._weeklyPlanningSubject.isStopped) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "OnLoginPlantDid",
            "subscribeEvents",
            "observable annullato"
          );

          this._weeklyPlanningSubject = new BehaviorSubject<
            Array<WeeklyPlanning>
          >(null);
          this.weeklyPlanningList = this._weeklyPlanningSubject.asObservable();
        }
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   *  Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  public Init() {
    if (!this.needToInitialize) {
      return;
    }

    /* Without a selected building there is no area to scope the request to. Leave needToInitialize
     * set so the next Init - once a building (hence an area) is available - actually loads the list,
     * instead of silently consuming the one-shot flag now and never retrying. */
    if (this.getCurrentIdArea() == null) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "Init",
        "No current building selected: weekly-planning load skipped"
      );
      return;
    }

    this.initializeweeklyPlanningList();

    this.needToInitialize = false;
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.needToInitialize = true;

    this.Init();
  }

  /**
   * Drop every trace of the list currently held, so nothing scoped to a building the user has left
   * survives into the next one.
   */
  public invalidateWeeklyPlanningList(): void {
    this._storageService.remove(AppSettings.KEY_WEEKLYPLANNING);

    this._weeklyPlanningStore = null;
    this._weeklyPlanningSubject.next(null);
    this.needToInitialize = true;
  }

  /**
   * Initialize weeklyPlanning list: first it read from storage (via getFromStorage() function)
   * then if storage read failed it tries to retrive data from API
   */
  private initializeweeklyPlanningList() {
    /* Reachable with no building selected (e.g. the OnComeBackOnline re-init firing while the user
     * is on the building list): without an area every request would go out as `?idArea=null`, so
     * skip the load entirely until a building is selected again. */
    if (this.getCurrentIdArea() == null) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "initializeweeklyPlanningList",
        "No current building selected: weekly-planning load skipped"
      );
      return;
    }

    this.getFromStorage().then((resultReadStorage: GenericApiData) => {
      if (!resultReadStorage) {
        this.GetweeklyPlanning();
      } else {
        this.emitCachedWeeklyPlanningList(resultReadStorage);
        this.GetweeklyPlanningByLastModify(resultReadStorage);
      }
    });
  }

  /**
   * Publish the cached list before the server is asked whether it is still current.
   *
   * The subject is seeded with null and every consumer filters that out, so holding the cached
   * payload back until the lastModified round-trip answered left the whole weekly-planning
   * section blank for as long as the request took. The refresh that follows overwrites this with
   * the server copy when it differs.
   *
   * @param weeklyPlanningStore the payload just read from local storage
   */
  private emitCachedWeeklyPlanningList(
    weeklyPlanningStore: GenericApiData
  ): void {
    if (!weeklyPlanningStore.data) {
      return;
    }
    this._weeklyPlanningStore = weeklyPlanningStore;
    this._weeklyPlanningSubject.next(weeklyPlanningStore.data);
  }

  private initializeweeklyPlanningActionList() {
    this.getActionsFromStorage().then((resultReadStorage: GenericApiData) => {
      if (!resultReadStorage) {
      } else {
      }
    });
  }

  //#region Mqtt addresses handling

  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_DB_WEEKLYPLANNING_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_WEEKLYPLANNING_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_mqttService.subscribeTopic - RESERVED_DB_WEEKLYPLANNING_CHANGED",
      "subscribeMQTTAddresses",
      "topic: " + returnedObservable
    );

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._weeklyPlanningStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          /** map operator allow to make parallel execution without cancelling event: when this fired multiple times
           * it perform execeution without waiting previous execution complete
           */
          if (message.length === 3) {
            const release = await this._mutex.acquire();

            try {
              const operationKind = parseInt(message[0]);
              const idWeeklyPlanning = parseInt(message[1]);
              const clientID = message[2];

              /** Finding operation kind: Insert, Update, Delete */
              if (operationKind === GenericOperationKind.INSERT) {
                const indexUpdateweeklyPlanning =
                  this._weeklyPlanningStore.data.findIndex(
                    (weeklyPlanning: WeeklyPlanning) =>
                      weeklyPlanning.id === idWeeklyPlanning
                  );
                if (indexUpdateweeklyPlanning < 0) {
                  const insertweeklyPlanning: WeeklyPlanning =
                    await this.GetweeklyPlanningByID(idWeeklyPlanning).catch(
                      (error) => {
                        return null;
                      }
                    );

                  if (insertweeklyPlanning) {
                    this._weeklyPlanningStore.data.push(insertweeklyPlanning);
                  } else {
                    throw new Error(
                      "OnInsertweeklyPlanning - Error retriving weeklyPlanning with id= " +
                      idWeeklyPlanning
                    );
                  }
                }
              } else if (operationKind === GenericOperationKind.UPDATE) {
                const indexUpdateweeklyPlanning =
                  this._weeklyPlanningStore.data.findIndex(
                    (weeklyPlanning: WeeklyPlanning) =>
                      weeklyPlanning.id === idWeeklyPlanning
                  );

                if (indexUpdateweeklyPlanning >= 0) {
                  const updateweeklyPlanning: WeeklyPlanning =
                    await this.GetweeklyPlanningByID(idWeeklyPlanning).catch(
                      (error) => {
                        return null;
                      }
                    );

                  if (updateweeklyPlanning) {
                    this._weeklyPlanningStore.data[indexUpdateweeklyPlanning] =
                      updateweeklyPlanning;
                  } else {
                    throw new Error(
                      "OnUpdateweeklyPlanning - Error retriving weeklyPlanning with id= " +
                      idWeeklyPlanning
                    );
                  }
                } else {
                  throw new Error(
                    "OnUpdateweeklyPlanning weeklyPlanning not found id= " +
                    idWeeklyPlanning
                  );
                }
              } else if (operationKind === GenericOperationKind.DELETE) {
                const currentweeklyPlanning: WeeklyPlanning =
                  this._weeklyPlanningStore.data.find(
                    (weeklyPlanning: WeeklyPlanning) =>
                      weeklyPlanning.id === idWeeklyPlanning
                  );

                this._weeklyPlanningStore.data =
                  this._weeklyPlanningStore.data.filter(
                    (weeklyPlanning: WeeklyPlanning) =>
                      weeklyPlanning.id !== idWeeklyPlanning
                  );
              }
              return idWeeklyPlanning + "|" + clientID;
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnweeklyPlanningDataChange - Incorrect format message"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDChanged) => {
          promiseIDChanged.then((IDChanged_ClientID) => {
            const values = IDChanged_ClientID.split("|");

            /** Update store with weeklyPlanning list changed and propagate modify */
            this._weeklyPlanningStore.lastAPIModified = moment().format(
              MomentFormats.MOMENT_DATETIME_FORMAT
            );
            this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);
            this._eventService.OnweeklyPlanningChange.next(IDChanged_ClientID);
            this.setToStorage(this._weeklyPlanningStore);
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress - RESERVED_DB_WEEKLYPLANNING_CHANGED",
            "subscribeMQTTAddresses",
            "incorrect message format: " + error
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion

    //#region Subscribing to RESERVED_DB_WEEKLYPLANNING_CALENDAR_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_WEEKLYPLANNING_CALENDAR_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._weeklyPlanningStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          if (message.length === 4) {
            const operationKind = parseInt(message[0]);
            const idCalendar = parseInt(message[1]);
            const id = parseInt(message[2]);
            const clientID = message[3];

            const indexWeekly: number =
              this._weeklyPlanningStore.data.findIndex(
                (weekly: WeeklyPlanning) => weekly.id === id
              );

            if (indexWeekly >= 0) {
              if (operationKind === GenericOperationKind.INSERT) {
                if (
                  this._weeklyPlanningStore.data[indexWeekly].calendarIDs ==
                  null
                ) {
                  this._weeklyPlanningStore.data[indexWeekly].calendarIDs = [];
                }
                /* Idempotent: the originating client already reflected this assignment locally
                 * (see addCalendarToStore) before publishing, so its own echo must not push a
                 * duplicate id. */
                if (
                  !this._weeklyPlanningStore.data[
                    indexWeekly
                  ].calendarIDs.includes(idCalendar)
                ) {
                  this._weeklyPlanningStore.data[indexWeekly].calendarIDs.push(
                    idCalendar
                  );
                }
              } else if (operationKind === GenericOperationKind.DELETE) {
                if (
                  this._weeklyPlanningStore.data[indexWeekly].calendarIDs ==
                  null
                ) {
                  this._weeklyPlanningStore.data[indexWeekly].calendarIDs = [];
                }
                this._weeklyPlanningStore.data[indexWeekly].calendarIDs =
                  this._weeklyPlanningStore.data[
                    indexWeekly
                  ].calendarIDs.filter(
                    (idWeeklyCalendar: number) =>
                      idWeeklyCalendar !== idCalendar
                  );
              }
            } else {
              throw new Error("OnWeeklyCalendarDataChange - Weekly not found");
            }

            return id + "|" + clientID;
          } else {
            throw new Error(
              "OnWeeklyCalendarDataChange - Incorrect format message"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDChanged) => {
          promiseIDChanged.then((IDChanged_ClientID) => {
            this._weeklyPlanningStore.lastAPIModified = moment().format(
              MomentFormats.MOMENT_DATETIME_FORMAT
            );
            this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);
            this._eventService.OnweeklyPlanningChange.next(IDChanged_ClientID);
            this.setToStorage(this._weeklyPlanningStore);
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress - RESERVED_DB_WEEKLYPLANNING_CALENDAR_CHANGED",
            "subscribeMQTTAddresses",
            "incorrect message format: " + error
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion

    //#region Subscribing to RESERVED_DB_WEEKLYPLANNINGACTION_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_WEEKLYPLANNINGACTION_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._weeklyPlanningStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          if (message.length === 3 && this._weeklyPlanningStore) {
            const id = parseInt(message[0]);
            const idAction = parseInt(message[1]);
            const clientID = message[2];

            return id + "|" + clientID;
          } else {
            throw new Error(
              "OnWeeklyPlanningActionChange - Incorrect format message"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDChanged) => {
          promiseIDChanged.then((IDChanged_ClientID) => {
            this._weeklyPlanningStore.lastAPIModified = moment().format(
              MomentFormats.MOMENT_DATETIME_FORMAT
            );
            this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);
            this._eventService.OnweeklyPlanningChange.next(IDChanged_ClientID);
            this.setToStorage(this._weeklyPlanningStore);
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress - RESERVED_DB_WEEKLYPLANNINGACTION_CHANGED",
            "subscribeMQTTAddresses",
            "Error: " + error
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }
  //
  //#endregion

  //#region weeklyPlanning API Operations
  /**
   *
   * @param idweeklyPlanning id weeklyPlanning to delete
   *
   * Delete weeklyPlanning item by id.
   * It cast Observable to Promise (with .toPromsie())
   * and then update private list, storage list and public observable list, otherwise return error.
   * As update and insert operation, store updates are made by mqtt event subscription
   */
  async DeleteweeklyPlanning(idweeklyPlanning: number): Promise<any> {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_WEEKLYPLANNING +
        "/" +
        idweeklyPlanning
      )
      .pipe(
        tap((cancel) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_WEEKLYPLANNING_CHANGED;

          const mqttMessage =
            GenericOperationKind.DELETE.toString() + "|" + idweeklyPlanning.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadWeeklyPlanning();

          /* Reflect the removal locally from the HTTP response instead of waiting for the
           * MQTT echo of our own DELETE, which is not a reliable self-refresh: this keeps
           * the list in sync immediately. Remote clients still receive the DELETE and resync. */
          this.removeWeeklyPlanningFromStore(idweeklyPlanning);
        })
      )
      .toPromise();
  }

  /**
   *
   * @param idweeklyPlanning id weeklyPlanning to duplicate
   *
   * DuplicateWeeklyPlanning weeklyPlanning.
   * It cast Observable to Promise (with .toPromsie())
   * and then update private list, storage list and public observable list, otherwise return error.
   */
  async DuplicateWeeklyPlanning(idweeklyPlanning: number): Promise<any> {
    return this._http
      .get(
        environment.httpServerRoot +
        AppSettings.API_URL_WEEKLYPLANNING_DUPLICATE +
        "/" +
        idweeklyPlanning
      )
      .pipe(
        tap((updateweeklyPlanning: WeeklyPlanning) => {
          /* Reflect the duplicated planning locally from the HTTP response, so the list shows it
           * immediately instead of depending on the MQTT echo of our own INSERT. Remote clients
           * still resync via the echo. */
          if (updateweeklyPlanning && updateweeklyPlanning.id > 0) {
            this.upsertWeeklyPlanningInStore(updateweeklyPlanning);
          }

          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_WEEKLYPLANNING_CHANGED;

          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            updateweeklyPlanning.id.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadWeeklyPlanning();
        })
      )
      .toPromise()
      .then(
        (duplicatedWeeklyPlanning: WeeklyPlanning) => {
          return duplicatedWeeklyPlanning;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   * Copy the configuration of a weekly planning from a day to others of the same weekly planning
   * @param idweeklyPlanning
   * @param dayFrom day used to start the copy
   * @param daysTo array of days used as destination of the copy
   */
  async CopyOnDays(
    idweeklyPlanning: number,
    dayFrom: number,
    daysTo: number[]
  ) {
    const copyData: WeeklyPlanningCopyData = {
      DayFrom: dayFrom,
      DaysTo: daysTo.join("_"),
      IDWeeklyPlanning: idweeklyPlanning,
    };
    return this._http
      .post(
        environment.httpServerRoot + AppSettings.API_URL_WEEKLYPLANNING_COPY,
        copyData
      )
      .pipe(
        tap((updateweeklyPlanning: WeeklyPlanning) => {
          /* Re-emit the list from the HTTP response so every open day-chart re-runs its
           * action fetch: the copy only mutates the actions of the target days server-side,
           * and the reserved MQTT reload has no local subscriber, so without this the charts
           * would keep showing the pre-copy configuration. */
          if (updateweeklyPlanning) {
            this.upsertWeeklyPlanningInStore(updateweeklyPlanning);
          }

          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_WEEKLYPLANNING_CHANGED;

          const mqttMessage =
            GenericOperationKind.UPDATE.toString() +
            "|" +
            updateweeklyPlanning.id.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadWeeklyPlanning();
        })
      )
      .toPromise()
      .then(
        (updateweeklyPlanning: WeeklyPlanning) => {
          return updateweeklyPlanning;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   *
   * @param weeklyPlanningData: data weeklyPlanning to update
   *
   * Update weeklyPlanning item by id.
   * As insert and delete operation, store updates are made by mqtt event subscription
   */
  async UpdateweeklyPlanning(
    idweeklyPlanning: number,
    weeklyPlanningData: iWeeklyPlanning
  ): Promise<WeeklyPlanning> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "UpdateweeklyPlanning",
      "IN UpdateweeklyPlanning. idweeklyPlanning: " + idweeklyPlanning
    );
    const indexweeklyPlanning = this._weeklyPlanningStore.data.findIndex(
      (weeklyPlanning) => idweeklyPlanning === weeklyPlanning.id
    );

    if (indexweeklyPlanning >= 0) {
      return this._http
        .put(
          environment.httpServerRoot +
          AppSettings.API_URL_WEEKLYPLANNING +
          "/" +
          idweeklyPlanning,
          weeklyPlanningData
        )
        .pipe(
          tap((updateweeklyPlanning: WeeklyPlanning) => {
            /* Reflect the update locally, so the list shows the new values immediately instead of
             * waiting for the (unreliable, for the originating client) MQTT echo of our own
             * UPDATE. Remote clients still resync via the echo. */
            this.upsertWeeklyPlanningInStore(
              this.resolveUpdatedWeeklyPlanning(
                idweeklyPlanning,
                weeklyPlanningData,
                updateweeklyPlanning
              )
            );

            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_WEEKLYPLANNING_CHANGED;

            /* The id is the one we were called with: reading it back from the response threw for
             * an answer that carried no entity, before anything was propagated at all. */
            const mqttMessage =
              GenericOperationKind.UPDATE.toString() +
              "|" +
              idweeklyPlanning.toString();

            /** Fire propagation to all connected client */
            this.publishAddress(mqttAddress, mqttMessage);

            this.SendReservedReloadWeeklyPlanning();
          })
        )
        .toPromise()
        .then(
          (updateweeklyPlanning: WeeklyPlanning) => {
            return updateweeklyPlanning;
          },
          (error) => {
            throw new Error(error);
          }
        );
    } else {
      return Promise.reject();
    }
  }

  /**
   *
   * @param idWeeklyPlanning id Current weekly planning to change active
   * @param active active value
   * This function make API call to change weekly planning active and then propagate changes via MQTT
   */
  async ChangeActiveWeeklyPlanning(idWeeklyPlanning: number, active: boolean) {
    const bodyRequest = {
      Active: active,
    };

    const indexWeeklyPlanning = this._weeklyPlanningStore.data.findIndex(
      (weekly) => idWeeklyPlanning === weekly.id
    );

    if (indexWeeklyPlanning >= 0) {
      return this._http
        .patch(
          environment.httpServerRoot +
          AppSettings.API_URL_WEEKLYPLANNING +
          "/" +
          idWeeklyPlanning,
          bodyRequest
        )
        .pipe(
          tap((insertWeekly: WeeklyPlanning) => {
            const id: number =
              this._weeklyPlanningStore.data[indexWeeklyPlanning].id;

            /* Reflect the new active state locally so the row updates immediately, instead
             * of waiting for the MQTT echo of our own change. */
            this._weeklyPlanningStore.data[indexWeeklyPlanning].active = active;
            this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);

            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_WEEKLYPLANNING_CHANGED;

            const mqttMessage =
              GenericOperationKind.UPDATE.toString() + "|" + id.toString();

            this.publishAddress(mqttAddress, mqttMessage);
            this.SendReservedReloadWeeklyPlanning();
          })
        )
        .toPromise()
        .then(
          (updateWeeklyPlanning: WeeklyPlanning) => {
            return;
          },
          (error) => {
            throw error;
          }
        );
    } else {
      return Promise.reject();
    }
  }

  /**
   * @param weeklyPlanningData: data weeklyPlanning to insert
   *
   * Insert new weeklyPlanning item.
   * As update and delete operation, store updates are made via mqtt subscription
   */
  async InsertweeklyPlanning(
    weeklyPlanningData: iWeeklyPlanning
  ): Promise<WeeklyPlanning> {
    return this._http
      .post<WeeklyPlanning>(
        environment.httpServerRoot + AppSettings.API_URL_WEEKLYPLANNING + "/",
        weeklyPlanningData
      )
      .pipe(
        tap((insertweeklyPlanning: WeeklyPlanning) => {
          /* Reflect the freshly created planning locally from the HTTP response, so the MQTT
           * echo of our own INSERT finds it already in the store (see the
           * RESERVED_DB_WEEKLYPLANNING_CHANGED handler) and skips the redundant GET-by-id.
           * Remote clients still receive the INSERT and fetch the entity themselves. */
          this.upsertWeeklyPlanningInStore(insertweeklyPlanning);

          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_WEEKLYPLANNING_CHANGED;

          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            insertweeklyPlanning.id.toString();

          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadWeeklyPlanning();
        })
      )
      .toPromise()
      .then(
        (insertweeklyPlanning: WeeklyPlanning) => {
          return insertweeklyPlanning;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "InsertweeklyPlanning",
            "Insert weeklyPlanning Error : " + error
          );

          throw new Error(error);
        }
      );
  }

  /**
   * Insert-or-replace a weekly-planning in the local store and notify subscribers.
   *
   * Lets a locally originated create be reflected immediately from its HTTP response,
   * without waiting for the MQTT echo to re-fetch the same entity by id: since the INSERT
   * handler only fetches ids not already present, the echo becomes a no-op for the
   * originating client while remote clients still resync normally.
   *
   * @param weeklyPlanning the weekly-planning to add or replace in the store
   */
  /**
   * Work out which weekly-planning to put in the store after an update.
   *
   * The endpoint is not guaranteed to answer with the saved entity — an empty 200 is a legitimate
   * answer here, see {@link GetweeklyPlanningByLastModify} — and an answer that carries none used
   * to leave the store untouched: the values were on the server but nothing that reads the list
   * (the list itself, the editor, the time-bands) knew about them until the page was reloaded.
   * In that case the payload the server has just accepted is merged onto the local copy.
   *
   * @param idWeeklyPlanning id of the updated weekly-planning
   * @param sentData the payload the server accepted
   * @param apiResponse whatever the endpoint answered with
   * @returns the weekly-planning to hand to {@link upsertWeeklyPlanningInStore}
   */
  private resolveUpdatedWeeklyPlanning(
    idWeeklyPlanning: number,
    sentData: iWeeklyPlanning,
    apiResponse: WeeklyPlanning
  ): WeeklyPlanning {
    if (apiResponse && apiResponse.id > 0) {
      return apiResponse;
    }

    const storedWeeklyPlanning: WeeklyPlanning =
      this._weeklyPlanningStore?.data?.find(
        (weeklyPlanning: WeeklyPlanning) =>
          weeklyPlanning.id === idWeeklyPlanning
      );

    return new WeeklyPlanning({
      ...storedWeeklyPlanning,
      ...sentData,
      id: idWeeklyPlanning,
    });
  }

  private upsertWeeklyPlanningInStore(weeklyPlanning: WeeklyPlanning): void {
    if (!this._weeklyPlanningStore) {
      return;
    }
    if (!this._weeklyPlanningStore.data) {
      this._weeklyPlanningStore.data = [];
    }

    const index: number = this._weeklyPlanningStore.data.findIndex(
      (item: WeeklyPlanning) => item.id === weeklyPlanning.id
    );
    if (index >= 0) {
      this._weeklyPlanningStore.data[index] = weeklyPlanning;
    } else {
      this._weeklyPlanningStore.data.push(weeklyPlanning);
    }

    this._weeklyPlanningStore.lastAPIModified = moment().format(
      MomentFormats.MOMENT_DATETIME_FORMAT
    );
    this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);
    this.setToStorage(this._weeklyPlanningStore);
  }

  /**
   * Remove a weekly-planning from the local store and notify subscribers.
   *
   * Counterpart of {@link upsertWeeklyPlanningInStore}: lets a locally originated delete be
   * reflected immediately from its HTTP response, without depending on the MQTT echo of our
   * own DELETE to refresh the list.
   *
   * @param idWeeklyPlanning id of the weekly-planning to remove
   */
  private removeWeeklyPlanningFromStore(idWeeklyPlanning: number): void {
    if (!this._weeklyPlanningStore || !this._weeklyPlanningStore.data) {
      return;
    }

    const index: number = this._weeklyPlanningStore.data.findIndex(
      (item: WeeklyPlanning) => item.id === idWeeklyPlanning
    );
    if (index < 0) {
      return;
    }

    this._weeklyPlanningStore.data = this._weeklyPlanningStore.data.filter(
      (item: WeeklyPlanning) => item.id !== idWeeklyPlanning
    );
    this._weeklyPlanningStore.lastAPIModified = moment().format(
      MomentFormats.MOMENT_DATETIME_FORMAT
    );
    this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);
    this.setToStorage(this._weeklyPlanningStore);
  }

  /**
   *
   * Get weeklyPlannings data from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetWeeklyPlanningListAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   *
   */
  public GetweeklyPlanning(): Subscription {
    const params: HttpParams = new HttpParams().set(
      "idArea",
      this.getCurrentIdArea()
    );

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_WEEKLYPLANNING,
        { params }
      )
      .subscribe(
        (returnweeklyPlanningStore: GenericApiData) => {
          this._isErrorInGetWeeklyPlanningListAPIRequest = false;

          this._weeklyPlanningStore = returnweeklyPlanningStore;

          this._weeklyPlanningSubject.next(returnweeklyPlanningStore.data);

          this.setToStorage(returnweeklyPlanningStore);
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "._http.get<GenericApiData>",
            "GetweeklyPlanning",
            "Errore.status: " + error.status
          );

          if (error.status === ApiErrorType.NOT_FOUND) {
            this._weeklyPlanningSubject.error(error);
          } else {
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              "._http.get<GenericApiData>",
              "GetweeklyPlanning",
              "Errore: " + error
            );

            this._isErrorInGetWeeklyPlanningListAPIRequest = true;
            this._weeklyPlanningStore = null;
            this._weeklyPlanningSubject.next([]);
          }
        }
      );
  }

  /**
   * Get weeklyPlannings data from API with last modify data store.
   * If api call fallback in error costructor, it propagate an Observable notification with .error() function
   * If original data store isn't changed, api return nothin (code 200)
   *
   * Called only right after {@link emitCachedWeeklyPlanningList}, so the cached payload is already
   * published: when the server answers that it is still current there is nothing to emit again.
   */
  private GetweeklyPlanningByLastModify(weeklyPlanningStore: GenericApiData) {
    const params: HttpParams = new HttpParams()
      .set("idArea", this.getCurrentIdArea())
      .set("lastModified", weeklyPlanningStore.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_WEEKLYPLANNING,
        { params }
      )
      .subscribe(
        (returnweeklyPlanningStore: GenericApiData) => {
          this._isErrorInGetWeeklyPlanningListAPIRequest = false;

          if (!returnweeklyPlanningStore) {
            /* Server confirmed the cached copy. Re-emitting it would hand every consumer a fresh
             * object reference for identical data, re-running the editor form's patchValue over
             * whatever the user started typing meanwhile. */
            this._weeklyPlanningStore = weeklyPlanningStore;
            return;
          }

          weeklyPlanningStore = returnweeklyPlanningStore;
          this.setToStorage(weeklyPlanningStore);

          this._weeklyPlanningStore = weeklyPlanningStore;
          this._weeklyPlanningSubject.next(weeklyPlanningStore.data);
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "._http.get<GenericApiData>",
            "GetweeklyPlanningByLastModify",
            "Errore.status: " + error.status
          );

          if (error.status === ApiErrorType.NOT_FOUND) {
            this._weeklyPlanningSubject.error(error);
          } else {
            this._isErrorInGetWeeklyPlanningListAPIRequest = true;
            //this._weeklyPlanningStore = null;
            this._weeklyPlanningSubject.next([]);
          }
        }
      );
  }

  /**
   * Get weeklyPlannings data from API. If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  private async GetweeklyPlanningByID(idBIGLogicweeklyPlanning: number) {
    const queryString: string = "?id=" + idBIGLogicweeklyPlanning;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_WEEKLYPLANNING +
        queryString
      )
      .pipe(
        switchMap((returnWeeklyPlanningList: GenericApiData) => {
          /** Clean API object from unnecessary data
           *  and check if data key is populated: if length is equal to zero, no
           *  weeklyPlanning is finded
           */
          if (returnWeeklyPlanningList.data.length > 0) {
            const returnweeklyPlanning: WeeklyPlanning =
              returnWeeklyPlanningList.data[0];

            return of(returnweeklyPlanning);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returnweeklyPlanning: WeeklyPlanning) => {
          return returnweeklyPlanning;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion

  ////#region
  /**
   *
   * @param idWeeklyPlanning weekly planning id
   * @param totalRow number of WeeklyPlanningAction desired
   * @param page page
   *
   * Get WeeklyPlanningActions data from API.
   */
  public async GetWeeklyPlanningActionByIDWeeklyPlanningAndOrDay(
    idWeeklyPlanning: number,
    day: number,
    totalRow: number,
    page: number,
    supervsionIntegrated: boolean = false
  ) {
    let params = new HttpParams();
    if (idWeeklyPlanning > 0) {
      params = params.append("idWeeklyPlanning", idWeeklyPlanning.toString());
    }
    if (day > 0) {
      params = params.append("day", day.toString());
    }
    params = params.append("total", totalRow.toString());
    params = params.append("page", page.toString());

    let returnActions = await this.GetWeeklyPlanningActionByParams(params);

    if (supervsionIntegrated) {
      const nextDay: number = day < 7 ? day + 1 : 1;
      const nextDayActions =
        await this.GetWeeklyPlanningActionByIDWeeklyPlanningAndOrDay(
          idWeeklyPlanning,
          nextDay,
          totalRow,
          page,
          false
        );
      returnActions = returnActions.concat(
        nextDayActions as Array<WeeklyPlanningAction>
      );
    }

    await this.updateWPDayFirstActionsMap(idWeeklyPlanning, returnActions);

    return returnActions;
  }

  /**
   * Retrieve from the service the first WeeklyPlanningActions (covering the temporal interval from 00:00 to 01:00)
   * associated with the day right after the one specified. These are read from {@link _weeklyPlanningsDaysFirstActionsMap},
   * which is kept up to date each time actions are fetched from the server. Used when a time-slice extends past midnight
   * and the command of the following day is needed.
   *
   * @param idWeeklyPlanning id of the weekly-planning the actions belong to
   * @param currentDay code of the current week-day (1: Monday, ..., 7: Sunday)
   * @returns the next-day first actions (a 4-element array of placeholder actions when that day has not been loaded yet)
   */
  public getNextDayFirstActions(
    idWeeklyPlanning: number,
    currentDay: number
  ): Array<WeeklyPlanningAction> {
    let nextDay = 1;
    if (currentDay < 7) {
      nextDay = currentDay + 1;
    }
    const nextdayIndex = nextDay - 1;

    const WPDayFirstActions =
      this._weeklyPlanningsDaysFirstActionsMap.get(idWeeklyPlanning) ??
      this.initialisenWeekDaysFirstActionList();

    return WPDayFirstActions[nextdayIndex];
  }

  /**
   * Initialise an empty 7x4 matrix of placeholder {@link WeeklyPlanningAction} objects: 7 rows (one per week-day, Monday
   * to Sunday) each holding 4 actions (the 00:00, 00:15, 00:30, 00:45 sectors). Used as the neutral starting value both
   * for the per-planning entries of {@link _weeklyPlanningsDaysFirstActionsMap} and for the temporary filtered matrix.
   * @returns the initialised matrix of placeholder actions
   */
  private initialisenWeekDaysFirstActionList(): Array<
    Array<WeeklyPlanningAction>
  > {
    let weekDays: WeeklyPlanningAction[][];
    weekDays = [];

    for (let wd = 0; wd < 7; wd++) {
      weekDays[wd] = [];
      for (let sa = 0; sa < 4; sa++) {
        weekDays[wd][sa] = new WeeklyPlanningAction();
      }
    }
    return weekDays;
  }

  /**
   * This method updates the map of the first WeeklyPlanningAction objects of each week-day with a list of WeeklyPlanningActions associated to
   * a specific Weekly-Planning. It does so filtering this list and selecting the elements with "progressivetime" of 0, 15, 30 or 45, then insering them
   * in order in a mirror matrix [week-days][lists of first-actions]. Then, if a column is not empty, it is used to update the corresponding column inside the
   * matrix contained inside the map for the specified "weekly-planning".
   * @param idWeeklyPlanning The id of the WeeklyPlanning object of interest.
   * @param weeklyPlanningActionFullList The full list of WeeklyPlanningAction, usually just retrieved from API. It could contains objects associated to
   * different week-days, but ALL MUST BE ASSOCIATED WITH THE SPECIFED WeeklyPlanning OBJECT!
   */
  private async updateWPDayFirstActionsMap(
    idWeeklyPlanning: number,
    weeklyPlanningActionFullList: Array<WeeklyPlanningAction>
  ) {
    let WPDayFirstActions =
      this._weeklyPlanningsDaysFirstActionsMap.get(idWeeklyPlanning);

    if (!WPDayFirstActions) {
      WPDayFirstActions = this.initialisenWeekDaysFirstActionList();
    }

    let WPDaysFilteredActions = this.initialisenWeekDaysFirstActionList();

    let actionCodeDay = 0;
    let indexFirstActions = 0;

    for (let unfilteredAction of weeklyPlanningActionFullList) {
      actionCodeDay = unfilteredAction.day - 1;

      if (unfilteredAction.progressivetime < 60) {
        if (unfilteredAction.progressivetime == 0) {
          indexFirstActions = 0;
        } else {
          indexFirstActions = unfilteredAction.progressivetime / 15;
        }

        WPDaysFilteredActions[actionCodeDay][indexFirstActions] =
          unfilteredAction;
      }
    }

    for (let weekDay = 0; weekDay < 7; weekDay++) {
      let meaningFulUpdate = false;
      let x = 0;
      while (x < 4 && !meaningFulUpdate) {
        meaningFulUpdate = WPDaysFilteredActions[weekDay][x].id > 0;
        x++;
      }

      if (meaningFulUpdate) {
        WPDayFirstActions[weekDay] = WPDaysFilteredActions[weekDay];
      }
    }

    this._weeklyPlanningsDaysFirstActionsMap.set(
      idWeeklyPlanning,
      WPDayFirstActions
    );
  }

  /**
   * Utility method, it retieves from API Server a list of "WeeklyPlanningAction" objects, filtered depending on a list of specific params.
   * @param APIparams The Params passed to the API call; they usually at least includes the ID of the "weekly-planning" selected and the code for
   * the week-day we are interested in.
   *
   * @returns The list of WeeklyPlanningActions from API server.
   */
  private async GetWeeklyPlanningActionByParams(
    APIparams: any
  ): Promise<WeeklyPlanningAction[]> {
    const returnActions = await this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_WEEKLYPLANNINGACTION,
        { params: APIparams }
      )
      .pipe(
        switchMap((returnWeeklyPlanningActionList: GenericApiData) => {
          if (returnWeeklyPlanningActionList.data.length > 0) {
            const actions: Array<WeeklyPlanningAction> =
              new Array<WeeklyPlanningAction>();
            for (
              let i = 0;
              i < returnWeeklyPlanningActionList.data.length;
              i++
            ) {
              actions.push(returnWeeklyPlanningActionList.data[i]);
            }

            return of(actions);
          } else {
            let emptyArray = new Array<WeeklyPlanningAction>();
            return of(emptyArray);
          }
        })
      )
      .toPromise();

    return returnActions ?? new Array<WeeklyPlanningAction>();
  }

  /**
   *
   * @param idWeeklyPlanningAction id WeeklyPlanningAction to update
   * @param weeklyPlanningActionData data weeklyPlanningAction to update
   *
   * Update weeklyPlanningAction item by id
   * As insert and delete operation, store updates are made by mqtt event subscription
   */
  public UpdateWeeklyPlanningAction(
    idWeeklyPlanningAction: number,
    weeklyPlanningActionData: iWeeklyPlanningAction
  ): Promise<WeeklyPlanningAction> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "UpdateWeeklyPlanningAction",
      "IN UpdateWeeklyPlanningAction"
    );

    return this._http
      .put(
        environment.httpServerRoot +
        AppSettings.API_URL_WEEKLYPLANNINGACTION +
        "/" +
        idWeeklyPlanningAction,
        weeklyPlanningActionData
      )
      .pipe(
        tap((updateWeeklyPlanningAction: WeeklyPlanningAction) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_WEEKLYPLANNINGACTION_CHANGED;

          const mqttMessage =
            GenericOperationKind.UPDATE.toString() +
            "|" +
            updateWeeklyPlanningAction.id.toString();

          this.publishAddress(mqttAddress, mqttMessage);

          const mqttLogicAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_BIGLOGICS_REFRESH;
          this.publishAddress(mqttLogicAddress, "");

          this.SendReservedReloadWeeklyPlanning();
        })
      )
      .toPromise()
      .then(
        (updateWeeklyPlanningAction: WeeklyPlanningAction) => {
          return updateWeeklyPlanningAction;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "UpdateWeeklyPlanningAction",
            "Errore: " + error
          );
          throw new Error(error);
        }
      );
  }

  /**
   *
   * @param weeklyPlanningActionData data WeeklyPlannigAction to insert
   *
   * Insert new WeeklyPlanningAction item.
   * As update and delete operation, store updates are made via mqtt subscription
   *
   */
  public async InsertWeeklyPlanningAction(
    weeklyPlanningActionData: iWeeklyPlanningAction
  ): Promise<WeeklyPlanningAction> {
    return this._http
      .post<WeeklyPlanningAction>(
        environment.httpServerRoot +
        AppSettings.API_URL_WEEKLYPLANNINGACTION +
        "/",
        weeklyPlanningActionData
      )
      .pipe(
        tap((insertWeeklyPlanningAction: WeeklyPlanningAction) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_WEEKLYPLANNINGACTION_CHANGED;

          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            insertWeeklyPlanningAction.id.toString();

          this.publishAddress(mqttAddress, mqttMessage);

          const mqttLogicAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_BIGLOGICS_REFRESH;
          this.publishAddress(mqttLogicAddress, "");

          this.SendReservedReloadWeeklyPlanning();
        })
      )
      .toPromise()
      .then(
        (insertWeeklyPlanningAction: WeeklyPlanningAction) => {
          return insertWeeklyPlanningAction;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.post<WeeklyPlanningAction>",
            "InsertWeeklyPlanningAction",
            "Error: " + error
          );
          throw new Error(error);
        }
      );
  }
  ////#endregion

  /////#region

  public async GetWeeklyPlanningCalendars(
    idWeeklyPlanning: number,
    countryCode: string
  ) {
    let params = new HttpParams();
    params = params.append("id", idWeeklyPlanning.toString());
    params = params.append("countryCode", countryCode);

    const returnCalendars = await this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_WEEKLYPLANNINGCALENDARS,
        { params: params }
      )
      .pipe(
        switchMap((returnWeeklyPlanningCalendarsList: GenericApiData) => {
          if (returnWeeklyPlanningCalendarsList.data.length > 0) {
            const calendars: any[] = [];
            for (
              let i = 0;
              i < returnWeeklyPlanningCalendarsList.data.length;
              i++
            ) {
              calendars.push(returnWeeklyPlanningCalendarsList.data[i]);
            }

            return of(calendars);
          } else {
            return [];
          }
        })
      )
      .toPromise();
    return returnCalendars;
  }

  /**
   *
   * @param idWeeklyPlanning id weeklyPlanning
   * @param calendarsAssignment list of calendars IDs to delete
   *
   * Delete relations between weeklyPlanning and calendars IDs
   */
  public async DeleteWeeklyPlanningCalendars(
    idWeeklyPlanning: number,
    calendarsAssignment: number
  ): Promise<any> {
    const indexweeklyPlanning: number =
      this._weeklyPlanningStore.data.findIndex(
        (weeklyPlanning) => idWeeklyPlanning === weeklyPlanning.id
      );
    if (indexweeklyPlanning >= 0) {
      const bodyRequest = {
        body: {
          ids: [calendarsAssignment],
        },
      };

      return this._http
        .request(
          "delete",
          environment.httpServerRoot +
          AppSettings.API_URL_WEEKLYPLANNING +
          "/" +
          idWeeklyPlanning +
          "/delete",
          bodyRequest
        )
        .pipe(
          tap((deleteCalendars) => {
            /* Reflect the removed calendar assignment locally from the HTTP response, so the editor
             * updates immediately instead of depending on the MQTT echo. */
            this.removeCalendarFromStore(idWeeklyPlanning, calendarsAssignment);

            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_WEEKLYPLANNING_CALENDAR_CHANGED;

            const mqttMessage =
              GenericOperationKind.DELETE.toString() +
              "|" +
              calendarsAssignment +
              "|" +
              idWeeklyPlanning;
            this.publishAddress(mqttAddress, mqttMessage);

            this.SendReservedReloadWeeklyPlanning();
          })
        )
        .toPromise();
    } else {
      return Promise.reject();
    }
  }

  /**
   *
   * @param idWeeklyPlanning id weeklyPlanning
   * @param calendarsAssignment list of calendars IDs to assign
   *
   * Assign weeklyplanning Ids to calendars
   */
  public async InsertWeeklyPlanningCalendars(
    idWeeklyPlanning: number,
    calendarsAssignment: number
  ): Promise<any> {
    const indexweeklyPlanning: number =
      this._weeklyPlanningStore.data.findIndex(
        (weeklyPlanning) => idWeeklyPlanning === weeklyPlanning.id
      );
    if (indexweeklyPlanning >= 0) {
      const bodyRequest = {
        ids: [calendarsAssignment],
      };
      return this._http
        .post(
          environment.httpServerRoot +
          AppSettings.API_URL_WEEKLYPLANNING +
          "/" +
          idWeeklyPlanning +
          "/assign",
          bodyRequest
        )
        .pipe(
          tap((insertCalendars) => {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_http.post",
              "InsertWeeklyPlanningCalendars",
              "calendar weekly planning api"
            );

            /* Reflect the new calendar assignment locally from the HTTP response, so the editor
             * updates immediately instead of depending on the MQTT echo. */
            this.addCalendarToStore(idWeeklyPlanning, calendarsAssignment);

            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_WEEKLYPLANNING_CALENDAR_CHANGED;

            const mqttMessage =
              GenericOperationKind.INSERT.toString() +
              "|" +
              calendarsAssignment +
              "|" +
              idWeeklyPlanning;
            this.publishAddress(mqttAddress, mqttMessage);

            this.SendReservedReloadWeeklyPlanning();
          })
        )
        .toPromise();
    } else {
      return Promise.reject();
    }
  }

  /**
   * Add a calendar id to a planning's {@link WeeklyPlanning.calendarIDs} in the local store and
   * notify subscribers. Mirrors the RESERVED_DB_WEEKLYPLANNING_CALENDAR_CHANGED handler so a locally
   * originated assignment is reflected from its HTTP response, without depending on the MQTT echo.
   * Null-safe no-op when the planning is not in the (area-scoped) store.
   */
  private addCalendarToStore(
    idWeeklyPlanning: number,
    idCalendar: number
  ): void {
    const storedWeeklyPlanning: WeeklyPlanning =
      this._weeklyPlanningStore?.data?.find(
        (weeklyPlanning: WeeklyPlanning) =>
          weeklyPlanning.id === idWeeklyPlanning
      );
    if (!storedWeeklyPlanning) {
      return;
    }

    if (!storedWeeklyPlanning.calendarIDs) {
      storedWeeklyPlanning.calendarIDs = [];
    }
    if (!storedWeeklyPlanning.calendarIDs.includes(idCalendar)) {
      storedWeeklyPlanning.calendarIDs.push(idCalendar);
    }

    this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);
    this.setToStorage(this._weeklyPlanningStore);
  }

  /**
   * Remove a calendar id from a planning's {@link WeeklyPlanning.calendarIDs} in the local store and
   * notify subscribers. Counterpart of {@link addCalendarToStore}; null-safe no-op when the planning
   * (or its calendar list) is not in the store.
   */
  private removeCalendarFromStore(
    idWeeklyPlanning: number,
    idCalendar: number
  ): void {
    const storedWeeklyPlanning: WeeklyPlanning =
      this._weeklyPlanningStore?.data?.find(
        (weeklyPlanning: WeeklyPlanning) =>
          weeklyPlanning.id === idWeeklyPlanning
      );
    if (!storedWeeklyPlanning || !storedWeeklyPlanning.calendarIDs) {
      return;
    }

    storedWeeklyPlanning.calendarIDs = storedWeeklyPlanning.calendarIDs.filter(
      (id: number) => id !== idCalendar
    );

    this._weeklyPlanningSubject.next(this._weeklyPlanningStore.data);
    this.setToStorage(this._weeklyPlanningStore);
  }

  /////#endregion

  //#region Storage
  /**
   * Read the cached list. The payload is tagged with the area it was fetched for
   * (see {@link setToStorage}); a tag that does not match the currently selected area is treated as
   * a cache miss, so switching building never surfaces another area's stale plannings.
   */
  protected getFromStorage(): Promise<object> {
    //get from storage and insert in local list of weeklyPlannings
    return this._storageService.get(AppSettings.KEY_WEEKLYPLANNING).then(
      (weeklyPlanningStorage: GenericApiData & { idArea?: number }) => {
        if (
          weeklyPlanningStorage &&
          weeklyPlanningStorage.idArea === this.getCurrentIdArea()
        ) {
          return weeklyPlanningStorage;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  protected setToStorage(weeklyPlanningStore: GenericApiData) {
    this._storageService.set(AppSettings.KEY_WEEKLYPLANNING, {
      ...weeklyPlanningStore,
      idArea: this.getCurrentIdArea(),
    });
  }

  protected getActionsFromStorage(): Promise<Object> {
    return this._storageService.get(AppSettings.KEY_WEEKLYPLANNINGACTION).then(
      (weeklyPlanningActionStorage: GenericApiData) => {
        if (weeklyPlanningActionStorage) {
          return weeklyPlanningActionStorage;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  protected setActionsToStorage(
    idWeeklyPlanning: string,
    weeklyPlanningActionStore: GenericApiData
  ) {
    this._storageService.set(
      AppSettings.KEY_WEEKLYPLANNINGACTION + "_" + idWeeklyPlanning,
      weeklyPlanningActionStore
    );
    const key: string = this._actionsKey.find(
      (actionKey) =>
        AppSettings.KEY_WEEKLYPLANNINGACTION + "_" + idWeeklyPlanning ===
        actionKey
    );

    if (key) {
      this._actionsKey.push(key);
    }
  }
  //#endregion

  //#region mqtt
  publishAddress(mqttAddress: string, value: string) {
    this._mqttService.publishAddress(mqttAddress, value);
  }
  //#endregion

  private SendReservedReloadWeeklyPlanning() {
    const mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_RELOAD_WEEKLY_PLANNING;

    this._mqttService.publishAddress(mqttAddress, "1", "REFRESH");
  }
}
